import css from './users.module.scss';

import { memo } from 'react';

import { useUserEditor } from 'com/UserEditor';
import { apiDeleteUser, apiLoginAs } from 'core/api';
import { ListPanel } from 'core/ui/ListPanel';
import { MenuItem } from 'core/ui/Menu';
import { useMessageModal } from 'core/ui/MessageModal';
import ICONS from 'core/ui/icons';

type TProps = {
  user: TRichUser;
  reloadUsers: () => void;
};

const UserPanel = ({ user, reloadUsers }: TProps) => {
  const { editUser } = useUserEditor();
  const { showError } = useMessageModal();

  const onRemove = async () => {
    try {
      if (!confirm('Are you sure?') && !confirm('Are you really sure?')) return;

      await apiDeleteUser(user.uid);
      reloadUsers();
    } catch (error) {
      showError(error);
    }
  };

  return (
    <ListPanel
      body={<UserInfo user={user} />}
      actions={[
        [
          {
            title: 'Edit',
            icon: ICONS.Edit,
            onClick: () =>
              editUser(user.uid).then(reloadUsers).catch(showError),
          },
        ],
        [
          {
            icon: ICONS.Menu,
            menuItems: (
              <>
                <MenuItem
                  label="Remove"
                  icon={ICONS.Trash}
                  onClick={onRemove}
                />
                <MenuItem
                  label={`Login as ${user.login}`}
                  icon={ICONS.Eye}
                  onClick={() =>
                    apiLoginAs(user.uid)
                      .then(() => location.replace('/'))
                      .catch(showError)
                  }
                />
              </>
            ),
          },
        ],
      ]}
    />
  );
};

export default memo(UserPanel);

function UserInfo({ user }: { user: TRichUser }) {
  return (
    <div className={css.info}>
      <dl>
        <dt>Login</dt>
        <dd>{user.login}</dd>
      </dl>

      {user.email && (
        <dl>
          <dt>Email</dt>
          <dd>{user.email}</dd>
        </dl>
      )}
      <dl>
        <dt>Role</dt>
        <dd>{user.isAdmin ? 'Admin' : 'User'}</dd>
      </dl>
      <dl>
        <dt>Words</dt>
        <dd>
          {user.decks} decks with {user.words.total} words{' '}
          {user.words.pending ? (
            <span className={css.pending}>(+{user.words.pending})</span>
          ) : null}
        </dd>
      </dl>
      <dl>
        <dt>Tests</dt>
        <dd>
          {user.tests} tests with {user.questions.total} questions{' '}
          {user.questions.pending ? (
            <span className={css.pending}>(+{user.questions.pending})</span>
          ) : null}
        </dd>
      </dl>
    </div>
  );
}
